import {
	Box,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Input,
	Stack,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { Form } from "../../../../components/form/form";
import { CountrySelector } from "../../../../components/country-selector/country-selector";
import { useTranslation } from "react-i18next";
import { useState, ChangeEvent, useEffect, useRef } from "react";
import { IsoCountryCode, ISystemBankAccount } from "../../../../models/user";
import { useRegionfunctions } from "../../../../region-functions/region-functions";
import { isNullOrWhiteSpace, keepDigits } from "../../../../utils/string-helper";
import { useUserContext } from "../../../../hooks/use-user-context";

type props = {
	systemBankAccount: ISystemBankAccount;
	setSystemBankAccount: (account: ISystemBankAccount) => void;
	disableForm?: boolean;
	setFormIsValid?: (isValid: boolean) => void;
};

export const BankInformationForm = ({
	systemBankAccount,
	setSystemBankAccount,
	disableForm = false,
	setFormIsValid,
}: props) => {
	const { t } = useTranslation();
	const { isImpersonating } = useUserContext();

	const ref = useRef<HTMLDivElement>(null);
	const [errorsState, setInternalErrorsState] = useState<{ [Key in keyof ISystemBankAccount]?: string }>();
	const [bankAccountState, setBankAccountState] = useState<ISystemBankAccount>(systemBankAccount);
	const [selectedCountry, setSelectedCountry] = useState<IsoCountryCode | "Other">(
		systemBankAccount.isoCountryCode,
	);
	const { regionFunctions } = useRegionfunctions(systemBankAccount.isoCountryCode);
	const allowedCountries: (keyof typeof IsoCountryCode | "Other")[] = [
		IsoCountryCode[systemBankAccount.isoCountryCode] as keyof typeof IsoCountryCode,
		"Other",
	];

	useEffect(() => {
		const isValid = () => {
			if (bankAccountState.disabled && bankAccountState.ibanDisabled) {
				return false;
			}

			const accoutValid =
				bankAccountState.disabled ||
				regionFunctions.validateAccountNumber(
					bankAccountState.clearingNumber + bankAccountState.accountNumber,
				);
			const IbanValid =
				bankAccountState.ibanDisabled ||
				regionFunctions.validateIban(
					bankAccountState.ibanAccountNumber,
					bankAccountState.allowedIbanCountries,
				);

			return accoutValid && IbanValid;
		};

		if (setFormIsValid) {
			setFormIsValid(isValid() && (errorsState === undefined || Object.keys(errorsState).length === 0));
		}
	}, [bankAccountState, regionFunctions, errorsState, setFormIsValid]);

	useEffect(() => {
		setSystemBankAccount(bankAccountState);
	}, [bankAccountState, setSystemBankAccount]);

	const onInternalChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name: field, value } = event.currentTarget;
		const valueWithOnlyNumbers = keepDigits(value);
		if (field === "accountNumber") {
			// Prevent input if max length is hit
			const hitMaxLength =
				valueWithOnlyNumbers.length >
				regionFunctions.bankAccountMeta(valueWithOnlyNumbers).accountNumberMaxLength;
			if (hitMaxLength) return;
			const clearingNumberLength = regionFunctions.bankAccountMeta(valueWithOnlyNumbers).clearingNumberLength;
			const clearingNumber = valueWithOnlyNumbers.substring(0, clearingNumberLength);
			const accountNumber = valueWithOnlyNumbers.substring(clearingNumberLength);
			setBankAccountState({ ...bankAccountState, clearingNumber, accountNumber });
			return;
		}

		setBankAccountState({ ...bankAccountState, [field]: value });
	};

	const onValidate = (field: keyof ISystemBankAccount) => {
		if (field === "accountNumber") {
			const error = !regionFunctions.validateAccountNumber(
				bankAccountState.clearingNumber + bankAccountState.accountNumber,
			);
			if (!error || isNullOrWhiteSpace(bankAccountState.clearingNumber)) {
				if (errorsState?.accountNumber) {
					delete errorsState.accountNumber;
					setInternalErrorsState({ ...errorsState });
				}
				return;
			}
			setInternalErrorsState({ ...errorsState, accountNumber: t("profile_bankAccountSettings_tooShort") });
			return;
		}
		const error = !regionFunctions.validateIban(
			bankAccountState.ibanAccountNumber,
			bankAccountState.allowedIbanCountries,
		);
		if (!error || isNullOrWhiteSpace(bankAccountState.ibanAccountNumber)) {
			if (errorsState?.ibanAccountNumber) {
				delete errorsState.ibanAccountNumber;
				setInternalErrorsState({ ...errorsState });
			}
			return;
		}
		setInternalErrorsState({
			...errorsState,
			ibanAccountNumber: t("profile_bankAccountSettings_invalidIban"),
		});
	};

	const getFields = (): (keyof ISystemBankAccount)[] => {
		const countriesWithOnlyAccountNumber = [IsoCountryCode.DK];

		if (countriesWithOnlyAccountNumber.includes(systemBankAccount.isoCountryCode)) return ["accountNumber"];

		return ["accountNumber", "ibanAccountNumber"];
	};

	const onSelectCountryForAccount = (countryCode: keyof typeof IsoCountryCode | "Other") => {
		if (countryCode === "Other") {
			setSelectedCountry(countryCode);
			setBankAccountState({ ...bankAccountState, accountNumber: "", clearingNumber: "" });

			if (errorsState) {
				delete errorsState.accountNumber;
				setInternalErrorsState({ ...errorsState });
			}
		} else {
			setSelectedCountry(IsoCountryCode[countryCode]);
		}
	};

	return (
		<Form formData={systemBankAccount} fields={getFields()}>
			{({ fields }) => {
				return (
					<Stack spacing={ThemeSpaceVariable.Medium}>
						{fields.map((field) => {
							const isAccountNrField = field === "accountNumber";
							const isDisabled =
								disableForm ||
								isImpersonating ||
								selectedCountry !== systemBankAccount.isoCountryCode ||
								(isAccountNrField
									? // Disable bank account field if IBAN is filled in - even if changes are un-saved
										systemBankAccount.disabled || (bankAccountState.ibanAccountNumber?.length ?? 0) > 0
									: // Disable IBAN field if bank account is filled in - even if changes are un-saved
										systemBankAccount.ibanDisabled || (bankAccountState.clearingNumber?.length ?? 0) > 0);
							return (
								<Box key={field}>
									<Box ref={ref}></Box>
									<FormControl
										zIndex={isAccountNrField ? 2 : 1}
										isDisabled={Boolean(
											systemBankAccount?.accountNumber || systemBankAccount?.ibanAccountNumber,
										)}
										isInvalid={errorsState ? Boolean(errorsState[field]) : false}
									>
										<FormLabel>{t(`profile_bankAccountSettings_${field}`)}</FormLabel>
										<Input
											name={field}
											leftAddon={
												isAccountNrField ? (
													<CountrySelector
														countries={allowedCountries}
														disabled={isDisabled}
														selectedCountry={
															selectedCountry !== "Other"
																? (IsoCountryCode[selectedCountry] as keyof typeof IsoCountryCode)
																: selectedCountry
														}
														onSelect={onSelectCountryForAccount}
														menuRef={ref}
													/>
												) : null
											}
											type={"string"}
											onChange={onInternalChange}
											onBlur={() => {
												onValidate(field);
											}}
											isDisabled={isDisabled}
											value={
												isAccountNrField
													? regionFunctions.formatAccountNumber(
															bankAccountState.clearingNumber ?? "",
															bankAccountState.accountNumber ?? "",
														)
													: String(bankAccountState[field])
											}
										/>
										<FormHelperText
											paddingBottom={ThemeSpaceVariable.Small}
											marginTop={ThemeSpaceVariable.Small}
										>
											{t(`profile_bankAccountSettings_helperText_${field}`)}
										</FormHelperText>
										<FormErrorMessage>{errorsState ? errorsState[field] : ""}</FormErrorMessage>
									</FormControl>
								</Box>
							);
						})}
					</Stack>
				);
			}}
		</Form>
	);
};
